import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List as ListIcon,
  ListOrdered,
  Pencil,
  Trash2,
  SmilePlus,
} from "lucide-react";
import AuthContext from "../context/AuthContext";
import { getErrorMessage } from "../services/api";
import commentsService from "../services/commentsService";
import reactionsService from "../services/reactionsService";
import ReactionBar from "./ReactionBar";
import { EmojiToolbarButton } from "./EmojiKeyboard";
import UserAvatar from "./UserAvatar";
import TranslatableText from "./TranslatableText";
import { useTranslation, useFormat } from "../i18n";

const PAGE_SIZE = 20;

const commentAuthorName = (a, t) =>
  a?.full_name || a?.email || t("card.unknown_user");

// Plain-text copy of a comment's HTML, used as the source for translation.
const htmlToPlainText = (html) => {
  if (!html) return "";
  if (typeof document === "undefined") {
    return String(html)
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.innerText || el.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

// `fmt` is the useFormat() bundle and `t` the feed-namespace translator of
// the calling component. Recent comments show a relative time; older ones a
// short date (with the year only when it differs from the current one).
const formatCommentTime = (ts, fmt, t) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return t("comments.just_now");
  const days = Math.round(mins / 60 / 24);
  if (days < 7) return fmt.relative(d);
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return fmt.date(d, opts);
};

// Editor limited to the comment toolbar's vocabulary. The server sanitizer is
// the security backstop; disabling the extra StarterKit nodes here just keeps
// the UX honest (no headings/quotes/code that would be stripped on save).
// StarterKit v3 bundles Underline, so it must not be added again (a duplicate
// extension breaks the editor schema and left the Post button stuck disabled).
const EDITOR_EXTENSIONS = (placeholder) => [
  StarterKit.configure({
    heading: false,
    blockquote: false,
    codeBlock: false,
    code: false,
    strike: false,
    horizontalRule: false,
  }),
  Placeholder.configure({ placeholder }),
];

const ToolbarButton = ({ active, onClick, label, buttonRef, children }) => (
  <button
    type="button"
    ref={buttonRef}
    aria-label={label}
    aria-pressed={active}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`w-7 h-7 flex items-center justify-center rounded text-sm ${
      active ? "bg-gray-200 text-gray-900" : "text-gray-500 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

// Must outlast the .comment-collapse transition in index.css (0.17s).
const COLLAPSE_MS = 200;

// Keeps children mounted through the closing transition so a panel or editor
// folds away instead of vanishing. `fallback` takes its place once the collapse
// has finished, which is how the composer swaps back to its trigger pill
// without the two ever overlapping.
const Collapse = ({ open, fallback = null, onCollapsed, children }) => {
  // `render` keeps the content mounted through the collapse; `entered` drives
  // the .is-open class that runs the expand/collapse transition.
  const [render, setRender] = useState(open);
  const [entered, setEntered] = useState(open);
  const ref = useRef(null);
  // Held in a ref so an inline callback can't retrigger the effect mid-collapse
  // and restart the timer, which would leave the content mounted forever.
  const onCollapsedRef = useRef(onCollapsed);
  useEffect(() => {
    onCollapsedRef.current = onCollapsed;
  });

  useEffect(() => {
    if (open) {
      setRender(true);
      return undefined;
    }
    setEntered(false);
    const t = setTimeout(() => {
      setRender(false);
      if (onCollapsedRef.current) onCollapsedRef.current();
    }, COLLAPSE_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Once mounted at 0fr, force a reflow and flip to .is-open so the height
  // transition has a committed starting frame — reliable even in a backgrounded
  // tab where requestAnimationFrame is throttled.
  useLayoutEffect(() => {
    if (open && render && !entered) {
      if (ref.current) void ref.current.offsetHeight;
      setEntered(true);
    }
  }, [open, render, entered]);

  if (!render) return fallback;

  return (
    <div
      ref={ref}
      className={`comment-collapse ${entered ? "is-open" : ""}`}
      aria-hidden={!open}
    >
      <div className="comment-collapse-inner">{children}</div>
    </div>
  );
};

function CommentComposer({
  initialHtml = "",
  placeholder: placeholderProp,
  submitLabel: submitLabelProp,
  accentColor = "#7c3aed",
  autoFocus = false,
  onSubmit,
  onCancel,
}) {
  const { t } = useTranslation("feed");
  const placeholder = placeholderProp || t("comments.placeholder");
  const submitLabel = submitLabelProp || t("comments.post");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS(placeholder),
    content: initialHtml,
    autofocus: autoFocus ? "end" : false,
    // @tiptap/react v3 defaults this off, so the component never re-rendered as
    // the user typed — editor.isEmpty stayed true and the Post button stayed
    // disabled. Re-render on each transaction so the button reflects content.
    shouldRerenderOnTransaction: true,
    // Clear a prior block/error message once the user starts editing again.
    onUpdate: () => setError(null),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[40px] px-3 py-2",
      },
    },
  });

  const submit = async () => {
    if (!editor || editor.isEmpty || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(editor.getHTML());
      editor.commands.clearContent();
    } catch (err) {
      // Keep the text so the user can edit it (e.g. a blocked-profanity 422).
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!editor) return null;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:border-gray-400 transition-colors">
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-gray-100">
        <ToolbarButton
          label={t("comments.bold")}
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          label={t("comments.italic")}
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <ItalicIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          label={t("comments.underline")}
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>
        <span className="w-px h-4 bg-gray-200 mx-1" />
        <ToolbarButton
          label={t("comments.bullet_list")}
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          label={t("comments.numbered_list")}
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <span className="w-px h-4 bg-gray-200 mx-1" />
        <EmojiToolbarButton
          editor={editor}
          renderButton={({ ref, onClick, active }) => (
            <ToolbarButton label={t("comments.add_emoji")} buttonRef={ref} active={active} onClick={onClick}>
              <SmilePlus className="w-4 h-4" />
            </ToolbarButton>
          )}
        />
      </div>
      <EditorContent editor={editor} />
      {error && (
        <div className="px-3 py-1.5 text-xs text-red-600 bg-red-50 border-t border-red-100">
          {error}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 px-2 py-1.5 border-t border-gray-100">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-full text-gray-600 hover:bg-gray-100"
          >
            {t("comments.cancel")}
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={busy || editor.isEmpty}
          className="px-4 py-1.5 text-sm font-medium rounded-full text-white transition-opacity disabled:opacity-50"
          style={{ background: accentColor }}
        >
          {busy ? t("comments.saving") : submitLabel}
        </button>
      </div>
    </div>
  );
}

// `editing` is owned by CommentSection rather than local state so an outside
// click can collapse an in-progress edit the same way it collapses the composer.
const CommentItem = ({
  comment,
  reactions,
  accentColor,
  editing,
  onEditingChange,
  onReactionsChange,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation("feed");
  const fmt = useFormat();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const safeHtml = DOMPurify.sanitize(comment.content_html || "");
  const plainText = htmlToPlainText(safeHtml);

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await onDelete(comment.id);
    } catch {
      // Delete failed; keep the comment and let the user retry.
      setDeleteBusy(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <div className="flex gap-2.5">
      <UserAvatar
        src={comment.author?.avatar_url}
        name={commentAuthorName(comment.author, t)}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
        textClassName="text-xs"
      />
      <div className="min-w-0 flex-1">
        <div className="bg-gray-50 rounded-2xl px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {commentAuthorName(comment.author, t)}
            </span>
            <span className="text-xs text-gray-400">
              {formatCommentTime(comment.created_at, fmt, t)}
              {comment.is_edited && t("comments.edited_suffix")}
            </span>
          </div>
          <Collapse
            open={editing}
            fallback={
              <TranslatableText
                text={plainText}
                className="prose prose-sm max-w-none text-gray-800 mt-0.5 break-words"
                render={(body) =>
                  body === plainText ? (
                    <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
                  ) : (
                    <div className="whitespace-pre-wrap">{body}</div>
                  )
                }
              />
            }
          >
            <div className="mt-1.5">
              <CommentComposer
                initialHtml={comment.content_html}
                submitLabel={t("comments.save")}
                accentColor={accentColor}
                autoFocus
                onCancel={() => onEditingChange(comment.id, false)}
                onSubmit={async (html) => {
                  await onEdit(comment.id, html);
                  onEditingChange(comment.id, false);
                }}
              />
            </div>
          </Collapse>
        </div>

        {!editing && (
          <div className="flex items-center gap-3 mt-1 pl-1">
            <div className="scale-90 origin-left">
              <ReactionBar
                targetType="comment"
                targetId={comment.id}
                initialReactions={reactions}
                accentColor={accentColor}
                onReactionsChange={(list) => onReactionsChange(comment.id, list)}
              />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {confirmingDelete ? (
                <>
                  <span className="text-xs text-gray-500">{t("comments.confirm_delete")}</span>
                  <button
                    type="button"
                    disabled={deleteBusy}
                    onClick={confirmDelete}
                    className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> {deleteBusy ? t("comments.deleting") : t("comments.delete")}
                  </button>
                  <button
                    type="button"
                    disabled={deleteBusy}
                    onClick={() => setConfirmingDelete(false)}
                    className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-50"
                  >
                    {t("comments.cancel")}
                  </button>
                </>
              ) : (
                <>
                  {comment.can_edit && (
                    <button
                      type="button"
                      onClick={() => onEditingChange(comment.id, true)}
                      className="text-xs text-gray-400 hover:text-gray-700 inline-flex items-center gap-1"
                    >
                      <Pencil className="w-3 h-3" /> {t("comments.edit")}
                    </button>
                  )}
                  {comment.can_delete && (
                    <button
                      type="button"
                      onClick={() => setConfirmingDelete(true)}
                      className="text-xs text-gray-400 hover:text-red-600 inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> {t("comments.delete")}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Controlled comment panel. The parent card owns the open/close state (the
// toggle lives in the card footer) and passes `open`; this renders only the
// thread + composer. The composer stays collapsed behind a "Write a comment…"
// trigger so opening the thread never auto-focuses an editor.
const CommentSection = ({
  targetType,
  targetId,
  accentColor = "#7c3aed",
  initialCount = 0,
  open = false,
  onCountChange,
  onEditorActiveChange,
  closeEditorsToken = 0,
}) => {
  const { t } = useTranslation("feed");
  const { user: currentUser } = useContext(AuthContext);
  const [items, setItems] = useState([]); // oldest → newest
  const [total, setTotal] = useState(initialCount);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [reactionsByComment, setReactionsByComment] = useState({});

  const fetchReactions = useCallback(async (comments) => {
    try {
      const summaries = await reactionsService.getSummary(
        "comment",
        comments.map((c) => c.id)
      );
      setReactionsByComment((prev) => {
        const next = { ...prev };
        summaries.forEach((s) => {
          next[s.target_id] = s.reactions;
        });
        return next;
      });
    } catch {
      /* reactions are non-critical */
    }
  }, []);

  // Cache each bar's authoritative summary. The thread unmounts its children on
  // collapse, so without this the viewer's own reaction would be lost on reopen.
  const handleReactionsChange = useCallback((commentId, list) => {
    setReactionsByComment((prev) => ({ ...prev, [commentId]: list }));
  }, []);

  const handleEditingChange = useCallback((commentId, on) => {
    setEditingId(on ? commentId : null);
  }, []);

  const loadPage = useCallback(
    async (offset) => {
      setLoading(true);
      try {
        const res = await commentsService.list(targetType, targetId, {
          limit: PAGE_SIZE,
          offset,
        });
        // Server returns newest-first; reverse each page to oldest→newest.
        const page = [...(res.items || [])].reverse();
        setTotal(res.total);
        setItems((prev) => (offset === 0 ? page : [...page, ...prev]));
        fetchReactions(res.items || []);
      } catch {
        /* keep whatever is loaded */
      } finally {
        setLoading(false);
        setLoadedOnce(true);
      }
    },
    [targetType, targetId, fetchReactions]
  );

  // Load the first page the first time the panel is opened.
  useEffect(() => {
    if (open && !loadedOnce) loadPage(0);
  }, [open, loadedOnce, loadPage]);

  // The composer rides open with the thread so the viewer can start typing the
  // moment they toggle comments — no extra click on the trigger pill.
  useEffect(() => {
    if (open) setComposerOpen(true);
  }, [open]);

  // Reset the editors only once the thread has finished collapsing, so they
  // don't visibly snap shut mid-animation.
  const handlePanelCollapsed = useCallback(() => {
    setComposerOpen(false);
    setEditingId(null);
  }, []);

  // Tell the parent card whether an editor is open, so its outside-click
  // handler collapses the editor first and the thread only on the next click.
  useEffect(() => {
    if (onEditorActiveChange) onEditorActiveChange(composerOpen || editingId !== null);
  }, [composerOpen, editingId, onEditorActiveChange]);

  // The parent bumps this token when an outside click should shut the editors.
  const seenCloseToken = useRef(closeEditorsToken);
  useEffect(() => {
    if (closeEditorsToken === seenCloseToken.current) return;
    seenCloseToken.current = closeEditorsToken;
    setComposerOpen(false);
    setEditingId(null);
  }, [closeEditorsToken]);

  // Keep the parent card's toggle count in sync with the live total.
  useEffect(() => {
    if (onCountChange) onCountChange(total);
  }, [total, onCountChange]);

  const handleCreate = async (html) => {
    setError(null);
    try {
      const created = await commentsService.create(targetType, targetId, html);
      setItems((prev) => [...prev, created]);
      setTotal((t) => t + 1);
    } catch (err) {
      // Surface the server's reason rather than silently dropping a comment the
      // user has already written.
      setError(err?.response?.data?.detail || t("comments.post_failed"));
      throw err;
    }
  };

  const handleEdit = async (commentId, html) => {
    const updated = await commentsService.update(commentId, html);
    setItems((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
  };

  const handleDelete = async (commentId) => {
    await commentsService.remove(commentId);
    setItems((prev) => prev.filter((c) => c.id !== commentId));
    setTotal((t) => Math.max(0, t - 1));
  };

  const hasMore = items.length < total;

  return (
    <Collapse open={open} onCollapsed={handlePanelCollapsed}>
      <div className="pt-3 px-1 sm:px-2 space-y-3">
      {currentUser && (
        <div className="flex gap-2.5">
          <UserAvatar
            src={currentUser.avatar_url}
            name={currentUser.full_name || currentUser.email || t("comments.you")}
            className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
            textClassName="text-xs"
          />
          <div className="flex-1 min-w-0">
            <Collapse
              open={composerOpen}
              fallback={
                <button
                  type="button"
                  onClick={() => setComposerOpen(true)}
                  className="composer-trigger-in w-full text-left px-4 py-2 rounded-full border border-gray-200 bg-white text-sm text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-colors"
                >
                  {t("comments.placeholder")}
                </button>
              }
            >
              <>
                <CommentComposer
                  accentColor={accentColor}
                  autoFocus
                  onCancel={() => setComposerOpen(false)}
                  onSubmit={handleCreate}
                />
                {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
              </>
            </Collapse>
          </div>
        </div>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() => loadPage(items.length)}
          disabled={loading}
          className="text-xs font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
        >
          {loading ? t("comments.loading") : t("comments.load_earlier")}
        </button>
      )}

      {loading && !loadedOnce ? (
        <p className="text-sm text-gray-400">{t("comments.loading_comments")}</p>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              reactions={reactionsByComment[c.id]}
              accentColor={accentColor}
              editing={editingId === c.id}
              onEditingChange={handleEditingChange}
              onReactionsChange={handleReactionsChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
          {loadedOnce && items.length === 0 && (
            <p className="text-sm text-gray-400">
              {t("comments.empty")}
            </p>
          )}
        </div>
      )}
      </div>
    </Collapse>
  );
};

export default CommentSection;
